import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function PortalAttendancePage({ token, user }) {
  const { language } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const studentId = user?.student_profile_id || user?.id || '';

  useEffect(() => { if (token && studentId) loadData(); else setLoading(false); }, [token]);

  const loadData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/parent-dashboard/attendance-calendar/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await res.json();
      if (d.success !== false) setData(d);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin h-10 w-10 border-b-2 border-orange-500 rounded-full"></div></div>;

  return (
    <div className="space-y-6" data-testid="portal-attendance-page">
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 text-white">
        <h1 className="text-xl font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>
          {language === 'hi' ? 'मेरी उपस्थिति / My Attendance' : 'My Attendance'}
        </h1>
        <p className="text-green-100 text-sm mt-1">{language === 'hi' ? 'महीने वार उपस्थिति रिकॉर्ड' : 'Month-wise attendance record'}</p>
      </div>

      {data?.calendar && Array.isArray(data.calendar) ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-xl p-4 border text-center">
              <p className="text-2xl font-bold text-green-600">{data.summary?.present || 0}</p>
              <p className="text-xs text-gray-400">{language === 'hi' ? 'उपस्थित' : 'Present'}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border text-center">
              <p className="text-2xl font-bold text-red-500">{data.summary?.absent || 0}</p>
              <p className="text-xs text-gray-400">{language === 'hi' ? 'अनुपस्थित' : 'Absent'}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border text-center">
              <p className="text-2xl font-bold text-blue-600">{data.summary?.percentage || 0}%</p>
              <p className="text-xs text-gray-400">{language === 'hi' ? 'प्रतिशत' : 'Percentage'}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border text-center">
              <p className="text-2xl font-bold text-purple-600">{data.summary?.total_days || 0}</p>
              <p className="text-xs text-gray-400">{language === 'hi' ? 'कुल दिन' : 'Total Days'}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border">
            <h3 className="font-semibold text-sm mb-3">{language === 'hi' ? 'इस महीने की उपस्थिति' : 'This Month'}</h3>
            <div className="flex flex-wrap gap-2">
              {data.calendar.slice(0, 31).map((day, i) => (
                <div key={i} className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs font-medium ${day.status === 'present' ? 'bg-green-100 text-green-700' : day.status === 'absent' ? 'bg-red-100 text-red-700' : 'bg-gray-50 text-gray-300'}`}>
                  {day.date?.split('-')[2] || i + 1}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl p-8 border text-center">
          <i className="fa-solid fa-calendar-check text-4xl text-gray-300 mb-3 block"></i>
          <p className="text-gray-400">{language === 'hi' ? 'उपस्थिति डेटा उपलब्ध नहीं' : 'No attendance data available'}</p>
          <p className="text-xs text-gray-300 mt-1">{language === 'hi' ? 'जब school attendance mark करेगा तो यहाँ दिखेगा' : 'Will appear when school marks attendance'}</p>
        </div>
      )}
    </div>
  );
}

export function PortalFeesPage({ token, user }) {
  const { language } = useLanguage();
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const schoolId = user?.school_id || '';

  useEffect(() => { if (token) loadData(); else setLoading(false); }, [token]);

  const loadData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/fees/?school_id=${schoolId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await res.json();
      setFees(d.fees || d.payments || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin h-10 w-10 border-b-2 border-orange-500 rounded-full"></div></div>;

  return (
    <div className="space-y-6" data-testid="portal-fees-page">
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-6 text-white">
        <h1 className="text-xl font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>
          {language === 'hi' ? 'मेरा शुल्क / My Fees' : 'My Fees'}
        </h1>
        <p className="text-amber-100 text-sm mt-1">{language === 'hi' ? 'Fee payment history और pending fees' : 'Payment history and pending fees'}</p>
      </div>

      {fees.length > 0 ? (
        <div className="space-y-3">
          {fees.map((f, i) => (
            <div key={i} className="bg-white rounded-xl p-4 border flex items-center justify-between" data-testid={`fee-item-${i}`}>
              <div>
                <p className="font-semibold text-sm text-gray-800">{f.fee_type || f.description || 'Fee Payment'}</p>
                <p className="text-xs text-gray-400">{f.month || f.period || ''} | {f.paid_at || f.date || ''}</p>
              </div>
              <div className="text-right">
                <p className="font-bold text-gray-800">₹{f.amount || 0}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${f.status === 'paid' || f.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                  {f.status === 'paid' || f.status === 'completed' ? (language === 'hi' ? 'भुगतान हो गया' : 'Paid') : (language === 'hi' ? 'बकाया' : 'Pending')}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl p-8 border text-center">
          <i className="fa-solid fa-indian-rupee-sign text-4xl text-gray-300 mb-3 block"></i>
          <p className="text-gray-400">{language === 'hi' ? 'कोई fee record नहीं' : 'No fee records'}</p>
        </div>
      )}
    </div>
  );
}

export function PortalAnnouncementsPage({ token, user }) {
  const { language } = useLanguage();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (token) loadData(); else setLoading(false); }, [token]);

  const loadData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/communication/announcements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await res.json();
      setAnnouncements(d.announcements || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const priorityColors = { urgent: 'bg-red-100 text-red-700 border-red-200', high: 'bg-orange-100 text-orange-700 border-orange-200', normal: 'bg-blue-100 text-blue-700 border-blue-200', low: 'bg-gray-100 text-gray-600 border-gray-200' };

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin h-10 w-10 border-b-2 border-orange-500 rounded-full"></div></div>;

  return (
    <div className="space-y-6" data-testid="portal-announcements-page">
      <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl p-6 text-white">
        <h1 className="text-xl font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>
          {language === 'hi' ? 'सूचनाएं / Announcements' : 'Announcements'}
        </h1>
        <p className="text-blue-100 text-sm mt-1">{language === 'hi' ? 'School की सभी सूचनाएं' : 'All school announcements'}</p>
      </div>

      {announcements.length > 0 ? (
        <div className="space-y-3">
          {announcements.map((a, i) => (
            <div key={i} className="bg-white rounded-xl p-4 border" data-testid={`announcement-${i}`}>
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold text-gray-800 text-sm">{a.title}</h3>
                <span className={`px-2 py-0.5 rounded-full text-xs border ${priorityColors[a.priority] || priorityColors.normal}`}>{a.priority || 'normal'}</span>
              </div>
              <p className="text-gray-600 text-sm">{a.message || a.content}</p>
              <p className="text-xs text-gray-300 mt-2">{a.created_at ? new Date(a.created_at).toLocaleDateString('hi-IN') : ''}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl p-8 border text-center">
          <i className="fa-solid fa-bullhorn text-4xl text-gray-300 mb-3 block"></i>
          <p className="text-gray-400">{language === 'hi' ? 'कोई सूचना नहीं' : 'No announcements'}</p>
        </div>
      )}
    </div>
  );
}

export function PortalComplaintsPage({ token, user }) {
  const { language } = useLanguage();
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: 'general' });

  useEffect(() => { if (token) loadData(); else setLoading(false); }, [token]);

  const loadData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/complaints/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await res.json();
      setComplaints(d.complaints || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const submitComplaint = async () => {
    try {
      await fetch(`${API_URL}/api/complaints/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      setShowForm(false);
      setForm({ title: '', description: '', category: 'general' });
      loadData();
    } catch (err) { console.error(err); }
  };

  const statusColors = { open: 'bg-yellow-100 text-yellow-700', in_progress: 'bg-blue-100 text-blue-700', resolved: 'bg-green-100 text-green-700', closed: 'bg-gray-100 text-gray-500' };

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin h-10 w-10 border-b-2 border-orange-500 rounded-full"></div></div>;

  return (
    <div className="space-y-6" data-testid="portal-complaints-page">
      <div className="bg-gradient-to-r from-purple-500 to-violet-600 rounded-2xl p-6 text-white flex justify-between items-center">
        <div>
          <h1 className="text-xl font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>
            {language === 'hi' ? 'शिकायतें / Complaints' : 'My Complaints'}
          </h1>
          <p className="text-purple-200 text-sm mt-1">{language === 'hi' ? 'शिकायत दर्ज करें और status track करें' : 'File complaints and track status'}</p>
        </div>
        <button onClick={() => setShowForm(true)} className="px-4 py-2 bg-white/20 text-white rounded-lg text-sm font-medium hover:bg-white/30 border border-white/30" data-testid="new-complaint-btn">
          + {language === 'hi' ? 'नई शिकायत' : 'New Complaint'}
        </button>
      </div>

      {complaints.length > 0 ? (
        <div className="space-y-3">
          {complaints.map((c, i) => (
            <div key={i} className="bg-white rounded-xl p-4 border" data-testid={`complaint-${i}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-gray-800 text-sm">{c.title}</h3>
                  <p className="text-gray-500 text-xs mt-1">{c.description?.substring(0, 100)}</p>
                  <p className="text-xs text-gray-300 mt-2">{c.created_at ? new Date(c.created_at).toLocaleDateString('hi-IN') : ''}</p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs ${statusColors[c.status] || statusColors.open}`}>{c.status || 'open'}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl p-8 border text-center">
          <i className="fa-solid fa-face-smile text-4xl text-green-300 mb-3 block"></i>
          <p className="text-gray-400">{language === 'hi' ? 'कोई शिकायत नहीं — सब ठीक है!' : 'No complaints — all good!'}</p>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full" data-testid="complaint-form-modal">
            <div className="p-5 border-b flex justify-between"><h2 className="font-bold">{language === 'hi' ? 'नई शिकायत' : 'New Complaint'}</h2><button onClick={() => setShowForm(false)} className="text-2xl text-gray-400">&times;</button></div>
            <div className="p-5 space-y-3">
              <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder={language === 'hi' ? 'शिकायत का विषय' : 'Subject'} data-testid="complaint-title" />
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="complaint-category">
                <option value="general">{language === 'hi' ? 'सामान्य' : 'General'}</option>
                <option value="academic">{language === 'hi' ? 'शैक्षिक' : 'Academic'}</option>
                <option value="transport">{language === 'hi' ? 'परिवहन' : 'Transport'}</option>
                <option value="fees">{language === 'hi' ? 'शुल्क' : 'Fees'}</option>
                <option value="infrastructure">{language === 'hi' ? 'बुनियादी ढांचा' : 'Infrastructure'}</option>
              </select>
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="w-full px-3 py-2 border rounded-lg h-24" placeholder={language === 'hi' ? 'विवरण लिखें...' : 'Description...'} data-testid="complaint-desc" />
            </div>
            <div className="p-5 border-t flex gap-3">
              <button onClick={() => setShowForm(false)} className="flex-1 py-2 border rounded-lg">Cancel</button>
              <button onClick={submitComplaint} disabled={!form.title} className="flex-1 py-2 bg-purple-500 text-white rounded-lg font-medium disabled:opacity-40" data-testid="submit-complaint-btn">{language === 'hi' ? 'दर्ज करें' : 'Submit'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
